import React from 'react'
import isEmpty from 'lodash/isEmpty'
import type { Product } from '@ztechapps/common.contract'
import { formatMoney } from '@ztechapps/common.utils'
import { Alert, Box, ShowIf, TooltipPopOver } from '@ztechapps/webui.util'
import { BusyButton } from '@ztechapps/webui.button'
import { useAiChatContext } from '../ai-chat/Context'
import styles from './ProductResultCards.module.scss'

export type ProductResultCardsProps = {
  products: Product[]
  fallbackApplied?: boolean
  relaxedFilters?: string[]
}

const IMAGE_PLACEHOLDER =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'><rect width='80' height='80' fill='%23e9ecef'/><text x='50%25' y='54%25' font-family='sans-serif' font-size='10' fill='%236c757d' text-anchor='middle'>No image</text></svg>"

export const ProductResultCards = ({ products, fallbackApplied, relaxedFilters }: ProductResultCardsProps) => {
  const { productUrl, helpAction } = useAiChatContext()
  const resolveUrl = productUrl ?? (() => '#')

  if (isEmpty(products)) {
    return (
      <div className="d-flex flex-column gap-2">
        <Alert type="info">
          <span className="text-body">No products matched your query.</span>
        </Alert>
        <ShowIf condition={!!helpAction}>
          <div>{helpAction}</div>
        </ShowIf>
      </div>
    )
  }

  return (
    <div className="d-flex flex-column gap-2">
      <ShowIf condition={!!fallbackApplied}>
        <Alert type="info">
          <span className="text-body">
            I couldn&apos;t find an exact match, here are similar products
            <ShowIf condition={!isEmpty(relaxedFilters)}>
              <> (relaxed filters: {(relaxedFilters ?? []).join(', ')})</>
            </ShowIf>
          </span>
        </Alert>
      </ShowIf>

      {products.map((product) => {
        const url = resolveUrl(product)
        const image = product.coverImageThumb || product.coverImage || IMAGE_PLACEHOLDER
        const name = product.name ?? 'Unnamed product'
        const code = product.code ?? ''
        const qtyOnHand = product.inventory?.quantityOnHand ?? 0
        const inStock = qtyOnHand > 0

        return (
          <Box key={product._id} className="bg-body">
            <TooltipPopOver tooltipBody={name}>
              <div className="d-flex gap-3 align-items-start">
                <img
                  src={image}
                  alt={name}
                  width={80}
                  height={80}
                  className="rounded border flex-shrink-0 object-fit-cover"
                />
                <div className={`d-flex flex-column flex-grow-1 gap-1 ${styles.flexTruncate}`}>
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div className={`fw-semibold text-body text-truncate flex-grow-1 ${styles.flexTruncate}`}>
                      {name}
                    </div>
                    <span
                      className={`badge flex-shrink-0 ${inStock ? 'bg-success-subtle text-success-emphasis' : 'bg-danger-subtle text-danger-emphasis'}`}
                    >
                      {inStock ? 'In stock' : 'Out of stock'}
                    </span>
                  </div>

                  <ShowIf condition={!isEmpty(code)}>
                    <div className="small text-muted text-truncate">{code}</div>
                  </ShowIf>

                  <div className="fw-semibold text-body">{formatMoney(product.price)}</div>

                  <div className="mt-2">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none"
                      aria-label={`View product ${name}`}
                    >
                      <BusyButton variant="outline-primary" bsSize="sm" text="View product" />
                    </a>
                  </div>
                </div>
              </div>
            </TooltipPopOver>
          </Box>
        )
      })}

      <ShowIf condition={!!helpAction}>
        <div>{helpAction}</div>
      </ShowIf>
    </div>
  )
}
